import * as React from "react";
import { useTranslation } from "react-i18next";
import type { ScheduleItem } from "src/types";

import AddToCalendar from "@/components/add-to-calendar/add-to-calendar-component";
import { RenderHTMLContent } from "@/components/editor/render-html-content/render-html-content-component";
import { AvatarGroup } from "@/components/editor/shared/schedules/avatar-group";
import { mapFirstLastNames } from "@/components/editor/shared/schedules/utils";
import { Typography } from "@/components/editor/shared/typography";
import { Calendar } from "@/components/icons/calendar";
import { useScheduleTimezone } from "@/hooks/use-schedule-timezone";
import getFormattedScheduleTime from "@/utils/dates/getFormattedScheduleTime";

import {
  ScheduleDisplayItemColumn,
  ScheduleDisplayItemWrapper,
  ScheduleItemType,
  StyledButton,
  StyledSegmentText,
} from "./default-schedule-component-styles";

type DefaultScheduleDisplayProps = {
  schedules: ScheduleItem[];
};

export default function DefaultScheduleDisplay(
  props: DefaultScheduleDisplayProps
) {
  return (
    <ul>
      {props.schedules?.map((schedule) => {
        return (
          <li
            data-testid="single-day-schedule"
            key={schedule.id}
            id={schedule.id.toString()}
          >
            <ScheduleDisplayItem schedule={schedule} />
          </li>
        );
      })}
    </ul>
  );
}

type ScheduleDisplayItemProps = {
  schedule: ScheduleItem;
};

function ScheduleDisplayItem(props: ScheduleDisplayItemProps) {
  const { t } = useTranslation();
  const selectedTimezone = useScheduleTimezone();
  const { time, type } = React.useMemo(() => {
    const type = props.schedule.event_part.event_part_type;
    const customText = props.schedule.event_part.event_type_custom_text;

    return {
      time: getFormattedScheduleTime(
        props.schedule.time_start,
        props.schedule.time_end,
        selectedTimezone
      ),
      type: customText ?? type.charAt(0).toUpperCase() + type.slice(1),
    };
  }, [
    props.schedule.event_part.event_part_type,
    props.schedule.event_part.event_type_custom_text,
    props.schedule.time_end,
    props.schedule.time_start,
    selectedTimezone,
  ]);

  const users = React.useMemo(() => {
    if (!props.schedule?.speakers) {
      return [];
    }
    return (
      mapFirstLastNames(props.schedule?.speakers).map((speaker) => ({
        firstName: speaker.first_name,
        lastName: speaker.last_name,
        image: speaker.image_url,
      })) || []
    );
  }, [props.schedule?.speakers]);

  return (
    <ScheduleDisplayItemWrapper>
      <ScheduleDisplayItemColumn>
        <ScheduleItemType data-testid="schedule-item-type">
          <StyledSegmentText>{type}</StyledSegmentText>
        </ScheduleItemType>
        <Typography pattern="labelOne" data-testid="schedule-item-time">
          {time}
        </Typography>
        <AddToCalendar
          event={{
            external_id: props.schedule.external_id,
            time_start: props.schedule.time_start,
            time_end: props.schedule.time_end,
            name: props.schedule.name,
          }}
        >
          <StyledButton
            data-testid="event-to-calendar-button"
            isInline
            variant="outlined"
            pattern="secondary"
            size="small"
            title={t("preference-panel.schedule.add-to-calendar")}
          >
            <Calendar className="mr-4 size-5" />
            {t("preference-panel.schedule.add-to-calendar")}
          </StyledButton>
        </AddToCalendar>
      </ScheduleDisplayItemColumn>
      <ScheduleDisplayItemColumn>
        <Typography pattern="headingFour" data-testid="schedule-item-name">
          {props.schedule.name}
        </Typography>
        <Typography
          as={RenderHTMLContent}
          pattern="description"
          data-testid="schedule-item-description"
          html={props.schedule.description}
        />
        {users.length > 0 && (
          <AvatarGroup
            popupLabel="Additional speakers"
            data-testid="speaker-avatars-group"
            users={users}
          />
        )}
      </ScheduleDisplayItemColumn>
    </ScheduleDisplayItemWrapper>
  );
}
